import { FC, MutableRefObject, useRef, useState, KeyboardEvent, FormEvent, useEffect } from 'react';
import { Box, Button, FormHelperText, TextField } from '@mui/material';
import { useInitialFocusRef } from '~/utils/useInitialFocusRef';
import { rEmail, SimpleFormContextProvider, SrRegion, Status, useLoginContext, useSimpleForm, useSimpleFormContext } from '~/pages/authentication/LoginContext';
import { useSaveContext } from 'ra-core';
import { useSimpleSaveAction } from '~/controller/SaveContextProvider';
import retrieveAxiosErr from '~/utils/retrieveAxiosErr';


const validators_1 = {
  password: (x:string) => {
    if (!x || x === '') { return 'Password is required' }
    if (x.length > 256) { return 'Must not exceed 255 characters' }
  }
}
const validators_2 = {
  ...validators_1,
  email: (x:string) => {
    if (!x || x === '') { return 'Email is required' }
    if (x.length > 256) { return 'Must not exceed 255 characters' }
    if (!rEmail.test(x)) {
      return 'Must be a valid email'
    }
  }
}

export const LoginJWT: FC<{ ssoUsername: string; }> = ({ ssoUsername }) => {
  const { states, Create, bag } = useLoginContext()
  const initalEmail : string = (bag ? bag['email'] : '') ?? '';
  const [localStates, setLocalStates] = useState({ email: (ssoUsername || initalEmail) ?? '', password: '' })

  const ctrl = useSimpleForm(ssoUsername ? validators_1 : validators_2)
  const { fieldProps } = ctrl
  
  const boxRef = useInitialFocusRef(50)
  const btnRef = useRef<HTMLButtonElement|null>(null)
  return (
    <SimpleFormContextProvider value={ctrl}>
      <SrRegion aria-labelledby="sign-in-title" boxRef={ctrl.parentNodeRef}>
        {ssoUsername ?
          <TextField
            id="email-static"
            disabled
            fullWidth
            label="Email Address"
            margin="normal"
            name="email"
            type="email"
            value={ssoUsername}
            variant="outlined"
          /> :
          <TextField id="login-email" label="Email Address"
            margin="normal" type="email" fullWidth
            inputRef={boxRef}
            autoComplete="off"
            {...fieldProps("email", (email) => setLocalStates(v => {
              if (bag) { bag.email = email }
              return ({...v, email})
            }))}
            inputProps={{ form: 'login-form' }}
            value={localStates.email}
          />
        }
        <TextField id="login-pass" label="Password"
          margin="normal" type="password" fullWidth variant="outlined"
          {...((() => {
            const props = fieldProps("password", (password) => setLocalStates(v => ({...v, password})))
            return { ...props, 
              onKeyDown: (ev: KeyboardEvent<HTMLInputElement>) => {
                if (ev.code === 'Enter' || ev.code === 'NumpadEnter') {
                  btnRef.current?.click()
                }
              } 
            }
          })())}
          inputProps={{ form: 'login-form' }}
          value={localStates.password}
        />
        {states.errMsg && (
          <Box sx={{ mt: 3 }}>
            <FormHelperText error>
              {states.errMsg}
            </FormHelperText>
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <Create resource="login" basePath="">
            <LocalLoginButton localStates={localStates} btnRef={btnRef} />
          </Create>
        </Box>
        <Box sx={{ mt: 2 }}>
        </Box>
      </SrRegion>
    </SimpleFormContextProvider>
  );
};

const LocalLoginButton : FC<{localStates: { email: string; password: string; }, btnRef: MutableRefObject<HTMLButtonElement|null>}> = ({
  localStates, btnRef
}) => {
  const localLogin = useSimpleSaveAction()
  const { saving: isSubmitting } = useSaveContext()
  const { dispatch } = useLoginContext()
  const { forceValidate } = useSimpleFormContext()

  const handleClick = (event:FormEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const formStates = forceValidate(localStates)
    if (Object.keys(formStates.errors).length > 0) { return }

    dispatch({ type: 'error_clear' })
    localLogin({ username: localStates.email, password: localStates.password } as any, {
      onSuccess: ({data}: {data:any}) => {
        dispatch({ type: 'p1_done', data: {
          phase1Result: data,
          username: localStates.email,
          password: localStates.password,
          status: Status.Phase2,
        }})
      },
      onFailure: (err:any) => {
        const errMsg = 
          (err?.response?.status === 429 || err?.body?.status === 429) 
          ? 'Too many login requests received. Please try again in a moment.' 
          : retrieveAxiosErr(err)
        dispatch({ type: 'error_occur', error: errMsg })
      }
    })
  }

  const formRef = useRef<HTMLFormElement|null>(null)
  useEffect(() => {
    setTimeout(() => {
      const fstElem : HTMLInputElement|null = formRef.current?.elements[0] as any
      fstElem?.focus()
    }, 200)
  }, [])

  return (
    <form id="login-form" onSubmit={handleClick} ref={formRef}>
      <Button
        ref={(x:any) => {btnRef.current = x}}
        color="primary"
        disabled={isSubmitting}
        fullWidth
        size="large"
        type="submit"
        variant="contained"
      >
        Log In
      </Button>
    </form>
  )
}
